<template>
    <div class="flex items-center gap-1">
        <button
            type="button"
            class="flex h-6 w-6 cursor-pointer items-center justify-center rounded-full transition disabled:cursor-not-allowed disabled:opacity-40"
            :class="
                rating === 'up'
                    ? 'bg-emerald-50 text-emerald-600'
                    : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
            "
            :title="t('vue.ai_chat_feedback_up')"
            :disabled="disabled || saving"
            @click="pick('up')"
        >
            <HandThumbUpIcon class="h-3.5 w-3.5" />
        </button>
        <button
            type="button"
            class="flex h-6 w-6 cursor-pointer items-center justify-center rounded-full transition disabled:cursor-not-allowed disabled:opacity-40"
            :class="
                rating === 'down'
                    ? 'bg-red-50 text-red-500'
                    : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
            "
            :title="t('vue.ai_chat_feedback_down')"
            :disabled="disabled || saving"
            @click="pick('down')"
        >
            <HandThumbDownIcon class="h-3.5 w-3.5" />
        </button>
    </div>

    <teleport to="body">
        <div
            v-if="reasonDialogOpen"
            class="fixed inset-0 z-[600] flex items-center justify-center bg-black/30 p-4"
            @click.self="closeReasonDialog"
        >
            <div class="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
                <h3 class="text-lg font-medium text-gray-800">
                    {{ t("vue.ai_chat_feedback_reason_title") }}
                </h3>
                <p class="mt-1 text-sm text-gray-500">
                    {{ t("vue.ai_chat_feedback_reason_hint") }}
                </p>
                <textarea
                    v-model="reason"
                    class="mt-4 w-full resize-none rounded-lg border border-gray-200 p-3 text-sm focus:border-[#FF421E] focus:ring-0"
                    rows="4"
                    :maxlength="REASON_MAX"
                    :placeholder="t('vue.ai_chat_feedback_reason_placeholder')"
                />
                <div class="mt-1 text-right text-xs text-gray-400">
                    {{ reason.length }} / {{ REASON_MAX }}
                </div>
                <div class="mt-4 flex justify-end gap-3">
                    <AtomicButton
                        color="colorless"
                        size="sm"
                        @click="closeReasonDialog"
                    >
                        {{ t("vue.cancel") }}
                    </AtomicButton>
                    <AtomicButton
                        color="yellow"
                        size="sm"
                        :disabled="saving || reason.trim() === ''"
                        @click="submitDown"
                    >
                        {{ t("vue.ai_chat_feedback_send") }}
                    </AtomicButton>
                </div>
            </div>
        </div>
    </teleport>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import axios from "axios";
import route from "ziggy-js";
import { HandThumbUpIcon, HandThumbDownIcon } from "@heroicons/vue/24/outline";
import { create } from "@/composables/useNotifications";
import { useTranslations } from "@/composables/useTranslations";
import type { IAiChatFeedback } from "@/types/AiChat/IAiChat";

const REASON_MAX = 1000;

const props = withDefaults(
    defineProps<{
        chatId: number;
        // Omitted for a whole-chat rating.
        messageId?: number | null;
        feedback?: IAiChatFeedback | null;
        disabled?: boolean;
    }>(),
    { messageId: null, feedback: null, disabled: false },
);

const emit = defineEmits<{ change: [value: IAiChatFeedback | null] }>();

const { t } = useTranslations();

const saving = ref(false);
const reasonDialogOpen = ref(false);
const reason = ref("");

const rating = computed(() => props.feedback?.rating ?? null);

const endpoint = computed(() =>
    props.messageId === null
        ? route("ai-chat.feedback.chat.store", { chat: props.chatId })
        : route("ai-chat.feedback.message.store", {
              chat: props.chatId,
              message: props.messageId,
          }),
);

const withdrawEndpoint = computed(() =>
    props.messageId === null
        ? route("ai-chat.feedback.chat.destroy", { chat: props.chatId })
        : route("ai-chat.feedback.message.destroy", {
              chat: props.chatId,
              message: props.messageId,
          }),
);

// Pressing the thumb that is already lit takes the rating back; the server owns
// that decision through a separate endpoint rather than a flag on the write.
const pick = async (next: "up" | "down"): Promise<void> => {
    if (rating.value === next) {
        await withdraw();
        return;
    }

    if (next === "down") {
        reason.value = props.feedback?.reason ?? "";
        reasonDialogOpen.value = true;
        return;
    }

    await send({ rating: "up" });
};

const submitDown = async (): Promise<void> => {
    await send({ rating: "down", reason: reason.value.trim() });
};

// The disabled attribute only takes effect after a re-render, so a double click
// lands both events before the button greys out. Both writers read the in-flight
// flag synchronously, which is what actually keeps one verdict to one request.
const send = async (payload: Record<string, string>): Promise<void> => {
    if (saving.value) {
        return;
    }

    saving.value = true;

    try {
        const response = await axios.post(endpoint.value, payload);
        emit("change", response.data?.feedback ?? null);
        reasonDialogOpen.value = false;
        reason.value = "";
    } catch {
        create({
            title: t("vue.ai_chat_feedback_failed"),
            text: "",
            type: "error",
        });
    } finally {
        saving.value = false;
    }
};

const withdraw = async (): Promise<void> => {
    if (saving.value) {
        return;
    }

    saving.value = true;

    try {
        await axios.delete(withdrawEndpoint.value);
        emit("change", null);
    } catch {
        create({
            title: t("vue.ai_chat_feedback_failed"),
            text: "",
            type: "error",
        });
    } finally {
        saving.value = false;
    }
};

const closeReasonDialog = (): void => {
    reasonDialogOpen.value = false;
    reason.value = "";
};
</script>
